import { externalLaunchContextAtom } from '@/atoms/externalLaunchAuthAtoms';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { useAtomValue } from 'jotai';

/**
 * Whether the app is currently running inside an EHR launch.
 *
 * Combines the two signals a launch can set. Either alone is enough. Use
 * `externalLaunchContextAtom.ehrProvider` if you need the specific provider.
 *
 * - `useIsFhirAuth()` — Redux `authProvider === FHIR`. NOT Epic-only, and NOT
 *   the same as "is SMART on FHIR": it means "externally launched token, bypass
 *   Kinde". Set by Epic (`useEpicLaunch`), Epic Haiku (`useHaikuLaunch`), and by
 *   Medtech and SGI Med (both `useMutateIntegrationLaunch`), which are not SMART
 *   launches but are still patient-scoped, since each resolves one patient at
 *   launch and aborts with `PATIENT_NOT_FOUND` if it cannot.
 * - `externalLaunchContextAtom.ehrProvider` — sessionStorage-backed, carries
 *   the provider name. NOT only the generic SMART flow: set by
 *   `useGenericEhrLaunch` (how Cerner and any newly onboarded SMART connector
 *   arrive), by `useOpenApiLaunch`, and by `useOnboardingHealthDirect`.
 *
 * What this does NOT catch, because neither signal is set: widget auth (sets
 * `AUTH0` / `KINDE`) and Medical Director (a persistence sidecar rather than a
 * provider, so the user keeps whatever they logged in with, see
 * `ExternalLaunchAuthState`). Every suppression built on this hook therefore
 * misses those two. Widen HERE rather than in a caller, so the suppressions stay
 * in step instead of drifting apart.
 *
 * Verified against the launch paths on 2026-08-21 (AGE-1961). Healthdirect is
 * the loosest fit, being telehealth rather than an embedded chart, and is in
 * scope by decision.
 */
export const useIsEhrLaunch = (): boolean => {
  const isFhirAuth = useIsFhirAuth();
  const externalLaunchContext = useAtomValue(externalLaunchContextAtom);
  return isFhirAuth || Boolean(externalLaunchContext?.ehrProvider);
};
